import { analyzeSemantics } from './promptArchitect/semanticAnalyzer';
import { interpretVisuals, interpretVisualsFromElements } from './promptArchitect/visualInterpreter';
import { buildProfessionalPrompt } from './promptArchitect/promptBuilder';
import type { BuiltPrompt, SemanticAnalysis } from './promptArchitect/types';

export interface SceneElements {
  subject: string;
  action: string;
  environment: string;
  timeLighting: string;
  moodStyle: string;
  // Compatibility fields used by existing UI integrations.
  subjects?: string[];
  actions?: string[];
  lighting?: string;
  mood?: string;
  style?: string;
  camera?: string;
  composition?: string;
}

export interface AnalysisResult {
  elements: SceneElements;
  confidence: number;
  suggestions: string[];
  semantic: SemanticAnalysis;
  promptParts: BuiltPrompt;
}

const missingSuggestions: Record<keyof SceneElements, string> = {
  subject: 'Add a clear main subject.',
  action: 'Describe what the subject is doing or the pose.',
  environment: 'Add where the scene happens.',
  timeLighting: 'Add time or lighting details.',
  moodStyle: 'Add mood or visual style direction.',
  subjects: '',
  actions: '',
  lighting: '',
  mood: '',
  style: '',
  camera: '',
  composition: '',
};

const mapToSceneElements = (semantic: SemanticAnalysis): SceneElements => ({
  subject: semantic.elements.subject,
  action: semantic.elements.action,
  environment: semantic.elements.environment,
  timeLighting: semantic.elements.timeLighting,
  moodStyle: semantic.elements.moodStyle,
  subjects: semantic.elements.subject ? [semantic.elements.subject] : [],
  actions: semantic.elements.action ? [semantic.elements.action] : [],
  lighting: semantic.elements.timeLighting,
  mood: semantic.elements.moodStyle,
  style: semantic.elements.moodStyle,
  camera: '',
  composition: '',
});

export class SmartSceneAnalyzer {
  analyzeScene(description: string): AnalysisResult {
    const semantic = analyzeSemantics(description);
    const visual = interpretVisuals(semantic);
    const promptParts = buildProfessionalPrompt(visual);
    const elements = mapToSceneElements(semantic);

    const suggestions = semantic.missing
      .map((key) => missingSuggestions[key as keyof SceneElements])
      .filter(Boolean);

    return {
      elements,
      confidence: semantic.confidence,
      suggestions,
      semantic,
      promptParts,
    };
  }

  generatePrompt(elements: SceneElements, _existingPrompt?: string): string {
    const visual = interpretVisualsFromElements({
      subject: elements.subject,
      action: elements.action,
      environment: elements.environment,
      timeLighting: elements.timeLighting,
      moodStyle: elements.moodStyle,
    });
    return buildProfessionalPrompt(visual).prompt;
  }

  convertToCanvasUpdates(elements: SceneElements): Record<string, unknown> {
    return {
      character: {
        subject: elements.subject || 'subject',
        pose: elements.action || 'standing',
      },
      environment: elements.environment || '',
      lighting: elements.timeLighting || '',
      style: elements.moodStyle || '',
    };
  }
}

export const smartSceneAnalyzer = new SmartSceneAnalyzer();
